import type React from "react";
import { ospThemes } from "@ospweb/sdk-ui";
import type { HeatmapConfig, HeatmapColorConfig, HeatmapNode } from "./types";

/** 默认边框圆角 */
export const DEFAULT_BORDER_RADIUS = "8px";

/**
 * Heatmap 颜色策略模块：
 * - 定义各档位的底色（涨/跌/0）
 * - 提供通用的 fill / text / border 样式计算函数
 * - 选中和 hover 态通过边框显示，不改变背景色，避免文字抖动
 */

/** 绿色区间（涨） */
const GREEN_BANDS = {
  LEVEL_5: ospThemes.colors.background_accent1_overlay80,
  LEVEL_4: ospThemes.colors.background_accent1_overlay70,
  LEVEL_3: ospThemes.colors.background_accent1_overlay50,
  LEVEL_2: ospThemes.colors.background_accent1_overlay30,
  LEVEL_1: ospThemes.colors.background_accent1_overlay10,
} as const;

/** 中性区间（0） */
const NEUTRAL = {
  ZERO: "rgba(255, 255, 255, 0.20)",
} as const;

/** 红色区间（跌） */
const RED_BANDS = {
  LEVEL_1: ospThemes.colors.background_accent2_overlay10,
  LEVEL_2: ospThemes.colors.background_accent2_overlay30,
  LEVEL_3: ospThemes.colors.background_accent2_overlay50,
  LEVEL_4: ospThemes.colors.background_accent2_overlay70,
  LEVEL_5: ospThemes.colors.background_accent2_overlay80,
} as const;

const DEFAULT_HEATMAP_COLORS: HeatmapColorConfig = {
  bands: {
    green: GREEN_BANDS,
    red: RED_BANDS,
    neutral: NEUTRAL,
  },
  hover: {
    overlayColor: "rgba(255, 255, 255, 0.08)",
  },
  text: {
    normal: "text-content_primary",
    hover: "text-content_primary",
    selected: "text-content_primary",
  },
  borderColor: "#ffffff",
  borderWidth: 2,
};

const SAT_THRESHOLD = 0.08;

/**
 * 合并外部传入的 colorConfig 与默认配置。
 * - 允许只覆盖部分档位或文本颜色
 */
function mergeColorConfig(config?: HeatmapConfig): HeatmapColorConfig {
  if (!config?.colorConfig) {
    return DEFAULT_HEATMAP_COLORS;
  }

  const overrides = config.colorConfig;
  return {
    bands: {
      green: {
        ...DEFAULT_HEATMAP_COLORS.bands.green,
        ...overrides.bands?.green,
      },
      red: { ...DEFAULT_HEATMAP_COLORS.bands.red, ...overrides.bands?.red },
      neutral: {
        ...DEFAULT_HEATMAP_COLORS.bands.neutral,
        ...overrides.bands?.neutral,
      },
    },
    hover: {
      ...DEFAULT_HEATMAP_COLORS.hover,
      ...(overrides.hover ?? {}),
    },
    text: {
      ...DEFAULT_HEATMAP_COLORS.text,
      ...(overrides.text ?? {}),
    },
    borderColor: overrides.borderColor ?? DEFAULT_HEATMAP_COLORS.borderColor,
    borderWidth: overrides.borderWidth ?? DEFAULT_HEATMAP_COLORS.borderWidth,
  };
}

/**
 * 计算普通态色块背景色。
 *
 * @description
 * - items.length >= minItemCountForPercentile：按涨 / 跌分组后按百分位分 5 档
 * - items.length < minItemCountForPercentile：使用线性透明度方案（abs(chg) / SAT_THRESHOLD）
 * - 选中态通过边框显示，不改变背景色
 */
export function getFillColor(params: {
  node: HeatmapNode;
  allItems: HeatmapNode[];
  config?: HeatmapConfig;
}): string {
  const { node, allItems, config } = params;
  const colors = mergeColorConfig(config);
  const chg = node.changePercent24h ?? 0;

  const positives = allItems.filter((n) => (n.changePercent24h ?? 0) > 0);
  const negatives = allItems.filter((n) => (n.changePercent24h ?? 0) < 0);

  const usePercentile =
    (config?.minItemCountForPercentile ?? 10) <= allItems.length;

  if (!usePercentile) {
    if (chg === 0) {
      return colors.bands.neutral.ZERO;
    }
    const abs = Math.abs(chg);
    const alpha = Math.min(abs / SAT_THRESHOLD, 1);
    if (chg > 0) {
      if (alpha > 0.8) {
        return colors.bands.green.LEVEL_5;
      }
      if (alpha > 0.6) {
        return colors.bands.green.LEVEL_4;
      }
      if (alpha > 0.4) {
        return colors.bands.green.LEVEL_3;
      }
      if (alpha > 0.2) {
        return colors.bands.green.LEVEL_2;
      }
      return colors.bands.green.LEVEL_1;
    }
    if (alpha > 0.8) {
      return colors.bands.red.LEVEL_5;
    }
    if (alpha > 0.6) {
      return colors.bands.red.LEVEL_4;
    }
    if (alpha > 0.4) {
      return colors.bands.red.LEVEL_3;
    }
    if (alpha > 0.2) {
      return colors.bands.red.LEVEL_2;
    }
    return colors.bands.red.LEVEL_1;
  }

  if (chg === 0 || (!positives.length && !negatives.length)) {
    return colors.bands.neutral.ZERO;
  }

  if (chg > 0 && positives.length) {
    const sorted = [...positives].sort(
      (a, b) => (b.changePercent24h ?? 0) - (a.changePercent24h ?? 0),
    );
    const index = sorted.findIndex((n) => n.id === node.id);
    const percentile = (index + 1) / sorted.length;
    if (percentile <= 0.1) {
      return colors.bands.green.LEVEL_5;
    }
    if (percentile <= 0.3) {
      return colors.bands.green.LEVEL_4;
    }
    if (percentile <= 0.6) {
      return colors.bands.green.LEVEL_3;
    }
    if (percentile <= 0.85) {
      return colors.bands.green.LEVEL_2;
    }
    return colors.bands.green.LEVEL_1;
  }

  if (chg < 0 && negatives.length) {
    const sorted = [...negatives].sort(
      (a, b) =>
        Math.abs(b.changePercent24h ?? 0) - Math.abs(a.changePercent24h ?? 0),
    );
    const index = sorted.findIndex((n) => n.id === node.id);
    const percentile = (index + 1) / sorted.length;
    if (percentile <= 0.1) {
      return colors.bands.red.LEVEL_5;
    }
    if (percentile <= 0.3) {
      return colors.bands.red.LEVEL_4;
    }
    if (percentile <= 0.6) {
      return colors.bands.red.LEVEL_3;
    }
    if (percentile <= 0.85) {
      return colors.bands.red.LEVEL_2;
    }
    return colors.bands.red.LEVEL_1;
  }

  return colors.bands.neutral.ZERO;
}

/**
 * 计算标题 / 数值的文字颜色 class。
 *
 * @description
 * - 选中态：始终使用 selected 颜色
 * - hover 态：使用 hover 颜色
 * - 普通态：使用 normal 颜色
 */
export function getTextColor(params: {
  isSelected: boolean;
  isHovered: boolean;
  config?: HeatmapConfig;
}): string {
  const { isSelected, isHovered, config } = params;
  const colors = mergeColorConfig(config);
  if (isSelected) {
    return colors.text.selected;
  }
  if (isHovered) {
    return colors.text.hover;
  }
  return colors.text.normal;
}

/**
 * 获取边框样式（用于选中和 hover 状态）
 * - 始终有边框，默认透明，选中或 hover 时改变颜色
 * - 四个角的元素需要单独处理，分别有一个圆角边框
 * - 一个元素可能占据多个角（如横跨整个顶部），需要同时设置多个圆角
 * - 这样可以避免选中时改变元素大小导致文字抖动
 */
export function getBorderStyle(params: {
  isSelected: boolean;
  isHovered: boolean;
  config?: HeatmapConfig;
  corner?: Array<
    "top-left" | "top-right" | "bottom-left" | "bottom-right"
  > | null;
  borderRadius?: string;
}): React.CSSProperties {
  const {
    isSelected,
    isHovered,
    config,
    corner,
    borderRadius = DEFAULT_BORDER_RADIUS,
  } = params;
  const colors = mergeColorConfig(config);
  const borderColor = colors.borderColor ?? "#ffffff";
  const borderWidth = colors.borderWidth ?? 2;

  // 始终有边框，未选中时使用透明，选中时使用配置的颜色
  const currentBorderColor =
    isSelected || isHovered ? borderColor : "transparent";

  const baseStyle: React.CSSProperties = {
    border: `${borderWidth}px solid ${currentBorderColor}`,
    boxSizing: "border-box",
  };

  // 根据占据的角计算 borderRadius
  // borderRadius 格式: top-left top-right bottom-right bottom-left
  if (corner && corner.length > 0) {
    const hasTopLeft = corner.includes("top-left");
    const hasTopRight = corner.includes("top-right");
    const hasBottomRight = corner.includes("bottom-right");
    const hasBottomLeft = corner.includes("bottom-left");

    baseStyle.borderRadius = `${hasTopLeft ? borderRadius : "0"} ${
      hasTopRight ? borderRadius : "0"
    } ${hasBottomRight ? borderRadius : "0"} ${
      hasBottomLeft ? borderRadius : "0"
    }`;
  }

  return baseStyle;
}
